import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useLoginModal } from "../components/LoginModalProvider"; // ADDED

/* ── Fonts ── */
const FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap";

function useFonts() {
  useEffect(() => {
    if (document.querySelector('link[href*="Instrument+Serif"]')) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = FONTS_URL;
    document.head.appendChild(l);
  }, []);
}

/* ── Tokens (mirrors LegalShell / HelpDisputePage) ── */
const T = {
  g700: "#155c36", g600: "#1b7a47", g500: "#1e9254", g200: "#a7d9bc",
  g100: "#d6f0e3", g50: "#eef8f3",
  n900: "#0f1117", n800: "#1c2130", n700: "#2d3549", n600: "#4a5268",
  n400: "#8a93a8", n200: "#d1d5e0", n150: "#e4e7ef", n100: "#eef0f5",
  n50: "#f7f8fa", white: "#ffffff",
  serif: "'Instrument Serif', Georgia, serif",
  sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
};

/* ── Inline SVG icons ── */
const Icon = ({ name, size = 20, color = "currentColor", strokeWidth = 1.6 }) => {
  const paths = {
    ArrowLeft: <><path d="M19 12H5" /><path d="m12 19-7-7 7-7" /></>,
    Phone: <><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.5 19.79 19.79 0 0 1 1.61 5a2 2 0 0 1 1.95-2H6.5a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 10.09a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" /></>,
    Shield: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></>,
    Lock: <><rect width="18" height="11" x="3" y="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>,
    Check: <><polyline points="20 6 9 12 4 10" /><path d="m9 12-2 5" /></>,
    CircleCheck: <><circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" /></>,
    Eye: <><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" /></>,
    EyeOff: <><path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" /><path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" /><path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" /><line x1="2" x2="22" y1="2" y2="22" /></>,
  };
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
};

/* ── OTP input — 6 boxes ── */
function OtpInput({ value, onChange, disabled }) {
  const inputs = useRef([]);
  const digits = (value || "").split("").concat(Array(6).fill("")).slice(0, 6);

  const handleKey = (e, i) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const next = digits.map((d, idx) => (idx === i ? "" : d)).join("");
      onChange(next);
      if (i > 0) inputs.current[i - 1]?.focus();
    }
  };

  const handleChange = (e, i) => {
    const val = e.target.value.replace(/\D/g, "").slice(-1);
    const next = digits.map((d, idx) => (idx === i ? val : d)).join("");
    onChange(next);
    if (val && i < 5) inputs.current[i + 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    onChange(pasted.padEnd(6, "").slice(0, 6).split("").map((d, i) => pasted[i] || "").join(""));
    inputs.current[Math.min(pasted.length, 5)]?.focus();
  };

  return (
    <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={el => (inputs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          disabled={disabled}
          onChange={e => handleChange(e, i)}
          onKeyDown={e => handleKey(e, i)}
          onPaste={handlePaste}
          style={{
            width: 48, height: 56, textAlign: "center",
            fontSize: 22, fontFamily: T.sans, fontWeight: 600,
            border: `1.5px solid ${d ? T.g500 : T.n150}`,
            borderRadius: 10, outline: "none", background: d ? T.g50 : T.white,
            color: T.n900, transition: "border-color .15s, background .15s",
            caretColor: T.g500,
          }}
          onFocus={e => (e.target.style.borderColor = T.g500)}
          onBlur={e => (e.target.style.borderColor = d ? T.g500 : T.n150)}
        />
      ))}
    </div>
  );
}

/* ── Password visibility toggle ── */
function PasswordInput({ value, onChange, placeholder, disabled }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="new-password"
        style={{
          width: "100%", padding: "13px 44px 13px 14px",
          border: `1.5px solid ${T.n150}`, borderRadius: 10,
          fontSize: 15, fontFamily: T.sans, color: T.n900,
          background: T.white, outline: "none",
          boxSizing: "border-box", transition: "border-color .15s",
        }}
        onFocus={e => (e.target.style.borderColor = T.g500)}
        onBlur={e => (e.target.style.borderColor = T.n150)}
      />
      <button
        type="button"
        onClick={() => setShow(s => !s)}
        style={{
          position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
          background: "none", border: "none", cursor: "pointer",
          color: T.n400, padding: 0, display: "flex", alignItems: "center",
        }}>
        <Icon name={show ? "EyeOff" : "Eye"} size={17} />
      </button>
    </div>
  );
}

/* ── Progress step pill ── */
function StepPill({ step }) {
  const steps = ["Phone", "Verify", "New password"];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 32 }}>
      {steps.map((label, i) => {
        const active = i === step;
        const done = i < step;
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", flex: i < steps.length - 1 ? 1 : "none" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{
                width: 24, height: 24, borderRadius: "50%",
                background: done ? T.g500 : active ? T.g100 : T.n100,
                border: `1.5px solid ${done ? T.g500 : active ? T.g500 : T.n200}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                {done
                  ? <Icon name="Check" size={13} color={T.white} strokeWidth={2.5} />
                  : <span style={{ fontSize: 11, fontWeight: 600, color: active ? T.g600 : T.n400 }}>{i + 1}</span>}
              </div>
              <span style={{ fontSize: 12, fontWeight: active ? 600 : 400, color: active ? T.n800 : T.n400, whiteSpace: "nowrap" }}>
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div style={{ flex: 1, height: 1, background: done ? T.g200 : T.n150, margin: "0 10px" }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── Main page ── */
export default function ForgotPasswordPage() {
  useFonts();
  const navigate = useNavigate();
  const { open: openLogin } = useLoginModal(); // ADDED

  const [step, setStep] = useState(0); // 0=phone, 1=otp, 2=newpass
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendTimer, setResendTimer] = useState(0);

  /* countdown for resend */
  useEffect(() => {
    if (resendTimer <= 0) return;
    const id = setTimeout(() => setResendTimer(t => t - 1), 1000);
    return () => clearTimeout(id);
  }, [resendTimer]);

  /* ── helpers ── */
  const normalisePhone = (raw) => {
    const digits = raw.replace(/\D/g, "");
    if (digits.startsWith("0") && digits.length === 10) return "+254" + digits.slice(1);
    if (digits.startsWith("254") && digits.length === 12) return "+" + digits;
    if (digits.startsWith("7") && digits.length === 9) return "+254" + digits;
    return "+" + digits;
  };

  /* ── step 0: send OTP ── */
  const handleSendOtp = async () => {
    setError("");
    const normalised = normalisePhone(phone);
    if (normalised.length < 12) { setError("Enter a valid Kenyan phone number."); return; }
    setLoading(true);
    const { error: err } = await supabase.auth.signInWithOtp({ phone: normalised });
    setLoading(false);
    if (err) { setError(err.message); return; }
    setResendTimer(60);
    setStep(1);
  };

  /* ── step 1: verify OTP ── */
  const handleVerifyOtp = async () => {
    setError("");
    if (otp.replace(/\D/g, "").length < 6) { setError("Enter the 6-digit code."); return; }
    setLoading(true);
    const { error: err } = await supabase.auth.verifyOtp({
      phone: normalisePhone(phone),
      token: otp,
      type: "sms",
    });
    setLoading(false);
    if (err) { setError("Invalid or expired code. Try again."); return; }
    setStep(2);
  };

  /* ── step 1: resend OTP ── */
  const handleResend = async () => {
    setError("");
    setOtp("");
    setLoading(true);
    const { error: err } = await supabase.auth.signInWithOtp({ phone: normalisePhone(phone) });
    setLoading(false);
    if (err) { setError(err.message); return; }
    setResendTimer(60);
  };

  /* ── step 2: set new password ── */
  const handleSetPassword = async () => {
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setLoading(true);
    const { error: err } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (err) { setError(err.message); return; }
    openLogin({ toast: "Password updated. Please sign in." }); // CHANGED — was navigate("/login", {...})
  };

  /* ── shared UI ── */
  const Btn = ({ children, onClick, disabled }) => (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      style={{
        width: "100%", padding: "14px 0",
        background: disabled || loading ? T.n150 : T.g500,
        color: disabled || loading ? T.n400 : T.white,
        border: "none", borderRadius: 12,
        fontSize: 15, fontWeight: 600, fontFamily: T.sans,
        cursor: disabled || loading ? "not-allowed" : "pointer",
        transition: "background .15s", letterSpacing: "0.01em",
      }}
      onMouseEnter={e => { if (!disabled && !loading) e.currentTarget.style.background = T.g600; }}
      onMouseLeave={e => { if (!disabled && !loading) e.currentTarget.style.background = T.g500; }}
    >
      {loading ? "Please wait..." : children}
    </button>
  );

  return (
    <div style={{ fontFamily: T.sans, background: T.n50, minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <style>{`* { box-sizing: border-box; } input:disabled { opacity: 0.55; }`}</style>

      {/* ── Nav ── */}
      <div style={{ padding: "20px 24px", borderBottom: `1px solid ${T.n100}`, background: T.white }}>
        <button
          onClick={() => (step > 0 ? setStep(s => s - 1) : navigate(-1))}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: "none", border: "none", cursor: "pointer",
            color: T.n400, fontSize: 13, fontFamily: T.sans, padding: 0,
          }}
          onMouseEnter={e => (e.currentTarget.style.color = T.n800)}
          onMouseLeave={e => (e.currentTarget.style.color = T.n400)}>
          <Icon name="ArrowLeft" size={14} strokeWidth={2} />
          {step > 0 ? "Back" : "Return to login"}
        </button>
      </div>

      {/* ── Card ── */}
      <div style={{ flex: 1, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "clamp(32px,5vw,56px) 20px" }}>
        <div style={{
          width: "100%", maxWidth: 440,
          background: T.white, borderRadius: 20,
          border: `1px solid ${T.n100}`,
          padding: "clamp(28px,6vw,44px) clamp(24px,5vw,40px)",
          boxShadow: "0 4px 24px rgba(15,17,23,.04)",
        }}>

          {/* Icon badge */}
          <div style={{
            width: 48, height: 48, borderRadius: 14,
            background: T.g50, border: `1.5px solid ${T.g100}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            marginBottom: 20,
          }}>
            <Icon name={step === 2 ? "Lock" : step === 1 ? "Shield" : "Phone"} size={22} color={T.g500} />
          </div>

          {/* Heading */}
          <h1 style={{ fontFamily: T.serif, fontSize: "clamp(26px,5vw,32px)", fontWeight: 400, margin: "0 0 6px", color: T.n900, letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            {step === 0 && <>Reset your <em style={{ fontStyle: "italic", color: T.g600 }}>password</em></>}
            {step === 1 && <>Verify your <em style={{ fontStyle: "italic", color: T.g600 }}>number</em></>}
            {step === 2 && <>Choose a new <em style={{ fontStyle: "italic", color: T.g600 }}>password</em></>}
          </h1>
          <p style={{ fontSize: 14, color: T.n400, margin: "0 0 28px", lineHeight: 1.6 }}>
            {step === 0 && "Enter your registered phone number and we'll send a verification code."}
            {step === 1 && <>We sent a 6-digit code to <strong style={{ color: T.n700 }}>{normalisePhone(phone)}</strong>. Enter it below.</>}
            {step === 2 && "Your identity is verified. Set a strong new password for your account."}
          </p>

          <StepPill step={step} />

          {/* ── Step 0: phone input ── */}
          {step === 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: T.n700, display: "block", marginBottom: 7 }}>
                  Phone number
                </label>
                <div style={{ position: "relative" }}>
                  <span style={{
                    position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
                    fontSize: 14, color: T.n400, pointerEvents: "none",
                  }}>🇰🇪</span>
                  <input
                    type="tel"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    placeholder="07XX XXX XXX"
                    autoFocus
                    onKeyDown={e => e.key === "Enter" && handleSendOtp()}
                    style={{
                      width: "100%", padding: "13px 14px 13px 38px",
                      border: `1.5px solid ${T.n150}`, borderRadius: 10,
                      fontSize: 15, fontFamily: T.sans, color: T.n900,
                      background: T.white, outline: "none", boxSizing: "border-box",
                    }}
                    onFocus={e => (e.target.style.borderColor = T.g500)}
                    onBlur={e => (e.target.style.borderColor = T.n150)}
                  />
                </div>
              </div>
              {error && <p style={{ fontSize: 13, color: "#c0392b", margin: 0 }}>{error}</p>}
              <Btn onClick={handleSendOtp} disabled={!phone.trim()}>Send verification code</Btn>
            </div>
          )}

          {/* ── Step 1: OTP ── */}
          {step === 1 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <OtpInput value={otp} onChange={setOtp} disabled={loading} />
              {error && <p style={{ fontSize: 13, color: "#c0392b", margin: 0, textAlign: "center" }}>{error}</p>}
              <Btn onClick={handleVerifyOtp} disabled={otp.replace(/\D/g, "").length < 6}>
                Verify code
              </Btn>
              <p style={{ fontSize: 13, color: T.n400, textAlign: "center", margin: 0 }}>
                Didn't receive it?{" "}
                {resendTimer > 0
                  ? <span style={{ color: T.n400 }}>Resend in {resendTimer}s</span>
                  : <button onClick={handleResend} style={{ background: "none", border: "none", cursor: "pointer", color: T.g600, fontSize: 13, fontFamily: T.sans, fontWeight: 600, padding: 0 }}>Resend code</button>
                }
              </p>
            </div>
          )}

          {/* ── Step 2: new password ── */}
          {step === 2 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: T.n700, display: "block", marginBottom: 7 }}>
                  New password
                </label>
                <PasswordInput value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 8 characters" disabled={loading} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: T.n700, display: "block", marginBottom: 7 }}>
                  Confirm password
                </label>
                <PasswordInput value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repeat your new password" disabled={loading} />
              </div>

              {/* strength hint */}
              {password.length > 0 && (
                <div style={{ display: "flex", gap: 4 }}>
                  {[1, 2, 3].map(lvl => {
                    const strength = password.length >= 12 ? 3 : password.length >= 8 ? 2 : 1;
                    const colors = { 1: "#e74c3c", 2: "#f39c12", 3: T.g500 };
                    return <div key={lvl} style={{ flex: 1, height: 3, borderRadius: 99, background: lvl <= strength ? colors[strength] : T.n150, transition: "background .2s" }} />;
                  })}
                </div>
              )}

              {error && <p style={{ fontSize: 13, color: "#c0392b", margin: 0 }}>{error}</p>}
              <Btn onClick={handleSetPassword} disabled={!password || !confirm}>
                Set new password
              </Btn>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}